import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Link, useNavigate } from 'react-router-dom';
import { CreditCard, ArrowLeft, Lock, Package, MapPin, User, CheckCircle } from 'lucide-react';
import { useEnhancedCart } from '@/hooks/useEnhancedCart';
import Header from '@/components/header/ModernHeader';
import Footer from '@/components/Footer';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import StripePaymentForm from '@/components/StripePaymentForm';
import { useOrderManagement } from '@/hooks/useOrderManagement';

interface CheckoutForm {
  email: string;
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  paymentMethod: string;
  saveInfo: boolean;
  sameAsBilling: boolean;
}

const Checkout = () => {
  const { items, total, itemCount, clearCart } = useEnhancedCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { createNewOrder, isCreating } = useOrderManagement();
  const [showStripeForm, setShowStripeForm] = useState(false);
  const [orderCompleted, setOrderCompleted] = useState(false);
  const [completedOrder, setCompletedOrder] = useState<any>(null);

  const [form, setForm] = useState<CheckoutForm>({
    email: user?.email || '',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    paymentMethod: 'stripe',
    saveInfo: false,
    sameAsBilling: true
  });

  const shippingCost = total > 100 ? 0 : 9.99;
  const tax = total * 0.08;
  const finalTotal = total + shippingCost + tax;

  const handleInputChange = (field: keyof CheckoutForm, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to complete your order.",
        variant: "destructive",
      });
      return;
    }

    // Validate form
    if (!form.firstName || !form.lastName || !form.email || !form.address || !form.city || !form.state || !form.zipCode) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    setShowStripeForm(true);
  };

  const prepareOrderData = () => ({
    userId: user?.id || null,
    customerEmail: form.email,
    customerName: `${form.firstName} ${form.lastName}`,
    totalAmount: finalTotal,
    paymentMethod: 'stripe',
    shippingAddress: JSON.stringify({
      firstName: form.firstName,
      lastName: form.lastName,
      address: form.address,
      city: form.city,
      state: form.state,
      zipCode: form.zipCode,
      country: form.country
    }),
    billingAddress: JSON.stringify(form.sameAsBilling ? {
      firstName: form.firstName,
      lastName: form.lastName,
      address: form.address,
      city: form.city,
      state: form.state,
      zipCode: form.zipCode,
      country: form.country
    } : {
      firstName: form.firstName,
      lastName: form.lastName,
      address: form.address,
      city: form.city,
      state: form.state,
      zipCode: form.zipCode,
      country: form.country
    }),
    cartItems: items.map(item => ({
      product_id: item.product_id,
      quantity: item.quantity,
      price: item.price
    }))
  });

  const handlePaymentSuccess = async (result: any) => {
    try {
      // Payment successful, creating order
      
      // Create real order in database
      const orderData = prepareOrderData();
      const orderResult = await createNewOrder(orderData);
      
      if (orderResult.success) {
        console.log('Order created successfully:', orderResult);
        
        // Clear the cart
        clearCart();
        
        // Set order completion state with real order data
        const completeOrderData = {
          ...result,
          orderId: orderResult.orderId,
          orderNumber: orderResult.orderNumber,
          items: items,
          shippingAddress: form,
          totalAmount: finalTotal,
          completedAt: new Date().toISOString()
        };
        
        setOrderCompleted(true);
        setCompletedOrder(completeOrderData);
        
        toast({
          title: "Order created successfully!",
          description: `Order ${orderResult.orderNumber} has been completed.`,
        });

        // Navigate to order confirmation with real order data
        navigate('/order-confirmation', {
          state: {
            orderId: orderResult.orderId,
            orderNumber: orderResult.orderNumber,
            customerName: orderData.customerName,
            customerEmail: orderData.customerEmail,
            totalAmount: finalTotal
          }
        });
      } else {
        console.error('Order creation failed:', orderResult.error);
        toast({
          title: "Order creation failed",
          description: orderResult.error || "Failed to create order. Please try again.",
          variant: "destructive",
        });
        setShowStripeForm(false);
      }
    } catch (error) {
      console.error('Error creating order:', error);
      toast({
        title: "Order creation failed",
        description: "An unexpected error occurred. Please contact support.",
        variant: "destructive",
      });
      setShowStripeForm(false);
    }
  };

  const handlePaymentError = (error: string) => {
    toast({
      title: "Payment failed",
      description: error,
      variant: "destructive",
    });
    setShowStripeForm(false);
  };

  const handleContinueShopping = () => {
    navigate('/products');
  };

  const handleViewOrders = () => {
    navigate('/dashboard?tab=orders');
  };

  if (items.length === 0 && !orderCompleted) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto text-center">
            <Package className="h-24 w-24 text-muted-foreground mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-foreground mb-4">Your cart is empty</h1>
            <p className="text-muted-foreground mb-8">
              Add some items to your cart before checking out.
            </p>
            <Button asChild size="lg">
              <Link to="/products">Start Shopping</Link>
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Order completion screen
  if (orderCompleted && completedOrder) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto text-center">
            <CheckCircle className="h-24 w-24 text-green-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-foreground mb-4">Order Complete!</h1>
            <p className="text-muted-foreground mb-6">
              Thank you for your purchase. Your order has been successfully processed.
            </p>
            
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Order Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span>Order Number:</span>
                  <span className="font-mono">{completedOrder.orderNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span>Order ID:</span>
                  <span className="font-mono text-sm">{completedOrder.orderId}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Amount:</span>
                  <span className="font-semibold">${completedOrder.totalAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span className="text-green-600 font-semibold">Order Created</span>
                </div>
                <div className="flex justify-between">
                  <span>Customer:</span>
                  <span>{completedOrder.customerName}</span>
                </div>
                <div className="flex justify-between">
                  <span>Email:</span>
                  <span>{completedOrder.customerEmail}</span>
                </div>
              </CardContent>
            </Card>
            
            <div className="space-y-4">
              <Button onClick={handleContinueShopping} size="lg" className="w-full sm:w-auto">
                Continue Shopping
              </Button>
              <Button onClick={handleViewOrders} variant="outline" size="lg" className="w-full sm:w-auto sm:ml-4">
                View Order History
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/cart">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Cart
            </Link>
          </Button>
          <h1 className="text-3xl font-bold text-foreground mb-2">Checkout</h1>
          <p className="text-muted-foreground">Complete your purchase securely</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Checkout Form */}
          <div className="space-y-6">
            {!showStripeForm ? (
              <form onSubmit={handleFormSubmit}>
                {/* Contact Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      Contact Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="email">Email address</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="john@example.com"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">First name</Label>
                        <Input
                          id="firstName"
                          required
                          value={form.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Last name</Label>
                        <Input
                          id="lastName"
                          required
                          value={form.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Shipping Address */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-5 w-5" />
                      Shipping Address
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="address">Address</Label>
                      <Input
                        id="address"
                        required
                        value={form.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        placeholder="123 Main St"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="city">City</Label>
                        <Input
                          id="city"
                          required
                          value={form.city}
                          onChange={(e) => handleInputChange('city', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="state">State</Label>
                        <Input
                          id="state"
                          required
                          value={form.state}
                          onChange={(e) => handleInputChange('state', e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="zipCode">ZIP Code</Label>
                        <Input
                          id="zipCode"
                          required
                          value={form.zipCode}
                          onChange={(e) => handleInputChange('zipCode', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="country">Country</Label>
                        <Input
                          id="country"
                          value={form.country}
                          onChange={(e) => handleInputChange('country', e.target.value)}
                          disabled
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Button type="submit" className="w-full" size="lg" disabled={isCreating}>
                  Continue to Payment
                </Button>
              </form>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Payment Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <StripePaymentForm
                    amount={finalTotal}
                    orderData={prepareOrderData()}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                  />
                  
                  <Button
                    variant="outline"
                    onClick={() => setShowStripeForm(false)}
                    className="w-full mt-4"
                    disabled={isCreating}
                  >
                    Back to Order Details
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Order Summary */}
          <div>
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Order Items */}
                <div className="space-y-3">
                  {items.map((item) => (
                    <div key={item.product_id} className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-muted rounded overflow-hidden">
                        {item.image_url ? (
                          <img
                            src={item.image_url}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                            📦
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.name}</p>
                        <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                      </div>
                      <p className="text-sm font-medium">
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>

                <Separator />

                {/* Price Breakdown */}
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal ({itemCount} items)</span>
                    <span>${total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Shipping</span>
                    <span>
                      {shippingCost === 0 ? (
                        <span className="text-green-600">FREE</span>
                      ) : (
                        `$${shippingCost.toFixed(2)}`
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tax</span>
                    <span>${tax.toFixed(2)}</span>
                  </div>
                </div>

                <Separator />

                <div className="flex justify-between text-lg font-semibold">
                  <span>Total</span>
                  <span>${finalTotal.toFixed(2)}</span>
                </div>

                {/* Security Notice */}
                <div className="bg-muted/50 p-3 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Lock className="h-4 w-4" />
                    <span>Secure Stripe Payment Processing</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Checkout;
